/**
 * País del edge, puesto al alcance del cliente (ADR-021 fase 3).
 *
 * `lib/request-country.ts` resuelve el país en el servidor, pero la divisa de
 * escaparate la elige un componente **cliente** (`package-selector`,
 * `landing-pricing`) en páginas que además queremos **estáticas**: las landings
 * son tráfico SEO, y hacerlas dinámicas por país las saca del CDN (el riesgo que
 * ADR-020 dejó anotado). Por eso el país viaja en una cookie que escribe el
 * middleware: el HTML sigue siendo el mismo para todos y solo el cliente ajusta
 * la divisa tras montar.
 *
 * **No es un gate y no puede serlo.** Una cookie la edita el usuario: leerla
 * como permiso sería un agujero. Aquí es inocuo por construcción — falsearla
 * solo cambia el precio que se PINTA, porque el cargo lo pone el producto del
 * proveedor (ADR-021 regla 1) y el trial se decide server-side con el header
 * (ADR-020). Cualquier decisión con dinero o coste detrás sigue leyendo el
 * header, nunca esto.
 */

import type { NextRequest, NextResponse } from 'next/server'
import { readCookie } from '@/lib/cookies'
import { parseCountryCode, type CountryCode } from '@/server/domain/country'

export const COUNTRY_COOKIE_NAME = 'pn_country'

/**
 * 30 días, como `auth-language`. El middleware la refresca cuando cambia el
 * país, así que en la práctica está siempre al día; la caducidad larga es para
 * quien entra directo a `/dashboard` (ruta excluida del middleware) tras cerrar
 * el navegador.
 */
export const COUNTRY_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60

/**
 * Adjunta el país a una respuesta del middleware **solo si aporta algo**.
 *
 * Dos casos en los que no se escribe, por el mismo motivo (no sobreescribir un
 * dato bueno con nada, ni añadir ruido a respuestas que queremos cacheables):
 *
 *  - **país desconocido**: sin GeoIP (dev local, algunos proxies) sobrescribir
 *    con vacío degradaría a EUR a alguien cuyo país ya conocíamos. Ausencia de
 *    dato ≠ dato nuevo.
 *  - **país sin cambios**: si no, cada pageview de una landing —justo las
 *    páginas que queremos servidas desde el CDN— arrastraría un `Set-Cookie`
 *    que no dice nada nuevo.
 *
 * La condición vive aquí, con la política, y no en el llamante.
 */
export function attachCountryCookie(
  request: NextRequest,
  response: NextResponse,
  country: CountryCode | null
): void {
  if (country === null) return
  if (request.cookies.get(COUNTRY_COOKIE_NAME)?.value === country) return

  response.cookies.set(COUNTRY_COOKIE_NAME, country, {
    path: '/',
    maxAge: COUNTRY_COOKIE_MAX_AGE_SECONDS,
    sameSite: 'lax',
    // Sin `httpOnly`: el paywall que la consume es cliente.
    // Sin `secure` explícito: Next lo deriva del protocolo, y en dev es http.
  })
}

/**
 * País escrito por el edge, o `null` si no hay cookie (o alguien la ha
 * manipulado). Se valida con el mismo parser del dominio que el header, así que
 * un valor raro se descarta en vez de colarse hasta la política.
 *
 * **Sin `decodeURIComponent` a propósito.** El espacio de valores es
 * `^[A-Z]{2}$`, así que aquí no hay nada que desescapar — y decodificar sí
 * tendría un coste: `decodeURIComponent('%')` lanza `URIError`, o sea que una
 * cookie manipulada («pn_country=%») reventaría en el efecto del hook justo en
 * el camino que este módulo promete blindar.
 */
export function readCountryCookie(): CountryCode | null {
  return parseCountryCode(readCookie(COUNTRY_COOKIE_NAME) ?? null)
}
